// ============================================================
// THE DISPLAY'S AMBIENT MARKS (plan design-gaps, T4; the display page draws
// them all, always - here each is the viewer's own switch, `mine-display`).
// On the floor, under the terrain: the actor's sight wedge toward the target
// (engine/display/ambient; the range rings went on the user's word,
// 2026-10-21). Over
// everything, the frame: fine scanlines and the edges sinking into the dark
// (the alarm tier's red vignette stays ruled out; this one is static). The
// band that swept down every 11 s went on the user's word (2026-10-20).
// ============================================================

import { memo, useId } from 'react';
import type { Camera } from '../../engine/battlefield/projection';
import { SCENE_VIEW, project } from '../../engine/battlefield/projection';
import { sightWedge } from '../../engine/display/ambient';
import { pointsAttr } from '../../engine/display/svgPath';
import type { AmbientMarks } from './displayFaces';
import styles from '../../styles/Display.module.css';

/**
 * The floor's mark: the actor's sight toward the target. Memoised on the marks and the camera: the scene re-renders
 * on every hover and beat.
 */
export const AmbientFloor = memo(function AmbientFloor({
  ambient,
  camera,
}: {
  ambient: AmbientMarks | null | undefined;
  camera: Camera;
}) {
  if (!ambient?.wedge) return null;
  const { wedge } = ambient;
  return (
    <g data-testid="display-ambient-floor" pointerEvents="none">
      {wedge && (
        <polygon
          data-testid="display-sight-wedge"
          className={styles.sightWedge}
          points={pointsAttr(sightWedge(wedge.from, wedge.toward).map((p) => project(p, camera, SCENE_VIEW)))}
        />
      )}
    </g>
  );
});

/** The frame over everything: the scanlines and the vignette. */
export function AmbientFrame({ ambient }: { ambient: AmbientMarks | null | undefined }) {
  const id = useId();
  if (!ambient?.scanlines && !ambient?.vignette) return null;
  const { width, height } = SCENE_VIEW;
  return (
    <g data-testid="display-ambient-frame" pointerEvents="none">
      <defs>
        <pattern id={`${id}-lines`} width={4} height={4} patternUnits="userSpaceOnUse">
          <rect className={styles.scanline} width={4} height={1} />
        </pattern>
        <radialGradient id={`${id}-vig`} cx="50%" cy="50%" r="72%">
          <stop offset="55%" className={styles.vignetteStop} stopOpacity={0} />
          <stop offset="100%" className={styles.vignetteStop} stopOpacity={0.7} />
        </radialGradient>
      </defs>
      {ambient.scanlines && (
        <rect data-testid="display-scanlines" width={width} height={height} fill={`url(#${id}-lines)`} />
      )}
      {ambient.vignette && (
        <rect data-testid="display-vignette" width={width} height={height} fill={`url(#${id}-vig)`} />
      )}
    </g>
  );
}
